// apps/web/src/shared/useProgress.ts
// ─────────────────────────────────────────────────────────────────────────
// Shared, lesson-level progress reads for every Learn view (Hub, Map,
// Schools, School, Playlists, Playlist, and now Lesson Library).
//
// REWIRED: the model + derivation now live in @nisk/core/progress. This file
// is a THIN web wrapper that (a) binds the localStorage-backed webProgressStore
// to the core functions, (b) preserves every export signature the existing
// callers already use, and (c) keeps the same-tab change event so views
// refresh. No consumer of this hook needs to change.
//
// Completion semantics changed underneath (LOCKED): a lesson is complete when
// readAt OR listenedAt is set. Because module status is derived from
// completion, listening a lesson now marks it complete across all Learn views —
// intended, audio is first-class.
// ─────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import {
	type CoarseStatus,
	getCompletedLessonIds as coreCompletedIds,
	getModuleCounts as coreModuleCounts,
	getModuleStatus as coreModuleStatus,
	isLessonComplete as coreIsLessonComplete,
	setLessonComplete as coreSetLessonComplete,
} from '@nisk/core/progress';
import { webProgressStore as store } from '../lib/progress/webProgressStore';

// Re-exported for callers that imported the type from here historically.
export type ModuleStatus = CoarseStatus;

// Same-tab change signal (storage events only fire cross-tab). Kept identical
// to the previous constant so lessonProgress writes + this hook stay in sync.
const PROGRESS_EVENT = 'nis:progress';

function emitProgressChange(): void {
	try {
		window.dispatchEvent(new Event(PROGRESS_EVENT));
	} catch {
		/* noop */
	}
}

// ── Stable readers (same signatures as before, store bound) ─────────────────

export function isLessonComplete(moduleId: string, lessonId: string): boolean {
	return coreIsLessonComplete(store, moduleId, lessonId);
}

export function markLessonComplete(
	moduleId: string,
	lessonId: string,
	done = true,
): void {
	coreSetLessonComplete(store, moduleId, lessonId, done);
	emitProgressChange();
}

export function getCompletedLessonIds(moduleId: string): string[] {
	return coreCompletedIds(store, moduleId);
}

export function getModuleCounts(moduleId: string): {
	done: number;
	total: number;
} {
	return coreModuleCounts(store, moduleId);
}

export function getModuleStatus(moduleId: string): ModuleStatus {
	return coreModuleStatus(store, moduleId);
}

// ── Hook ────────────────────────────────────────────────────────────────────
export function useProgress() {
	const [version, setVersion] = useState(0);

	useEffect(() => {
		const bump = (): void => setVersion((v) => v + 1);
		window.addEventListener('storage', bump);
		window.addEventListener(PROGRESS_EVENT, bump);
		return () => {
			window.removeEventListener('storage', bump);
			window.removeEventListener(PROGRESS_EVENT, bump);
		};
	}, []);

	return {
		version,
		isLessonComplete: useCallback(isLessonComplete, [version]),
		getCompletedLessonIds: useCallback(getCompletedLessonIds, [version]),
		getModuleCounts: useCallback(getModuleCounts, [version]),
		getModuleStatus: useCallback(getModuleStatus, [version]),
		markLessonComplete: useCallback(markLessonComplete, []),
	};
}
